import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  // Data
  entityList: null,
  entityListCountryCode: null,
  tanListByEntity: {},
  // Per-entity metadata from TAN API (tanApplicable, uniqueIdentificationNumber)
  entityMetaByUuid: {},

  // Period options from API — keyed by countryCode to avoid stale cache on country switch
  periodOptions: null, // { yearType, years, yearLabels, months }
  periodOptionsCountryCode: null, // tracks which country the cached options belong to
  periodOptionsLoading: false,
  periodOptionsError: null,

  // Selections
  selectedEntityUuid: '',
  selectedTanUuid: '',
  selectedYear: '',
  selectedMonths: [], // JSON strings

  // Loading
  entityLoading: false,
  tanLoading: false,

  // Errors
  entityError: null,
  tanError: null,

  // View trigger (increments on View button click)
  viewTrigger: 0,
}

const mtEntityTanSlice = createSlice({
  name: 'mtEntityTan',
  initialState,
  reducers: {
    // Entity
    fetchMTEntityStart: state => {
      state.entityLoading = true
      state.entityError = null
    },
    fetchMTEntitySuccess: (state, action) => {
      state.entityLoading = false
      state.entityList = action.payload.content
      state.entityListCountryCode = action.payload.countryCode
    },
    fetchMTEntityFailure: (state, action) => {
      state.entityLoading = false
      state.entityError = action.payload
    },

    // TAN
    fetchMTTanStart: state => {
      state.tanLoading = true
      state.tanError = null
    },
    fetchMTTanSuccess: (state, action) => {
      state.tanLoading = false
      state.tanListByEntity[action.payload.entityUuid] = action.payload.tanList
      // Store entity metadata (tanApplicable, uniqueIdentificationNumber)
      if (action.payload.entityMeta) {
        state.entityMetaByUuid[action.payload.entityUuid] =
          action.payload.entityMeta
      }
    },
    fetchMTTanFailure: (state, action) => {
      state.tanLoading = false
      state.tanError = action.payload
    },

    // Period options
    fetchMTPeriodOptionsStart: state => {
      state.periodOptionsLoading = true
      state.periodOptionsError = null
      state.periodOptions = null
      state.periodOptionsCountryCode = null
    },
    fetchMTPeriodOptionsSuccess: (state, action) => {
      state.periodOptionsLoading = false
      state.periodOptions = action.payload.data
      state.periodOptionsCountryCode = action.payload.countryCode
    },
    fetchMTPeriodOptionsFailure: (state, action) => {
      state.periodOptionsLoading = false
      state.periodOptionsError = action.payload
    },
    // Invalidate cached period options so the next fetch hits the API.
    // Dispatched after a data upload so newly uploaded years/periods appear
    // without requiring a full page reload.
    invalidateMTPeriodOptions: state => {
      state.periodOptions = null
      state.periodOptionsCountryCode = null
      state.periodOptionsError = null
    },

    // Selections
    setMTSelectedEntity: (state, action) => {
      state.selectedEntityUuid = action.payload
      state.selectedTanUuid = ''
    },
    setMTSelectedTan: (state, action) => {
      state.selectedTanUuid = action.payload
    },
    setMTSelectedYear: (state, action) => {
      state.selectedYear = action.payload
      state.selectedMonths = []
    },
    setMTSelectedMonths: (state, action) => {
      state.selectedMonths = action.payload
    },

    // Auto-select UIN when tanApplicable is false
    setMTAutoSelectedUIN: (state, action) => {
      state.selectedTanUuid = action.payload
    },

    // View trigger
    triggerMTView: state => {
      state.viewTrigger += 1
    },

    // Reset
    resetMTEntityTan: () => initialState,
  },
})

export const {
  fetchMTEntityStart,
  fetchMTEntitySuccess,
  fetchMTEntityFailure,
  fetchMTTanStart,
  fetchMTTanSuccess,
  fetchMTTanFailure,
  fetchMTPeriodOptionsStart,
  fetchMTPeriodOptionsSuccess,
  fetchMTPeriodOptionsFailure,
  invalidateMTPeriodOptions,
  setMTSelectedEntity,
  setMTSelectedTan,
  setMTSelectedYear,
  setMTSelectedMonths,
  setMTAutoSelectedUIN,
  triggerMTView,
  resetMTEntityTan,
} = mtEntityTanSlice.actions

export default mtEntityTanSlice.reducer
